import type { EventModuleKey, ResolvedEventModule } from '@conference/shared';

export type WelcomeFeatureStepKind =
  | 'welcome'
  | 'agenda'
  | 'ai'
  | 'library'
  | 'explore';

export interface WelcomeFeatureStep {
  kind: WelcomeFeatureStepKind;
  title: string;
  description: string;
}

function enabledModules(
  modules: readonly ResolvedEventModule[],
  keys: readonly EventModuleKey[],
): ResolvedEventModule[] {
  const allowed = new Set(keys);
  return modules.filter((module) => module.enabled && allowed.has(module.key));
}

function humanList(labels: readonly string[]): string {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels.at(-1)}`;
}

export function buildEventPreviewDescription(
  modules: readonly ResolvedEventModule[],
): string {
  const browseable = enabledModules(modules, [
    'agenda',
    'papers',
    'speakers',
    'exhibitors',
    'attendees',
  ]);
  const browseCopy = browseable.length > 0
    ? `You can browse ${humanList(browseable.map((module) => module.label))}.`
    : 'You can preview this event.';
  const ai = modules.find((module) => module.key === 'ai_copilot');
  const memberCopy = ai?.enabled
    ? `Join as a member to unlock ${ai.label}.`
    : 'Join as a member to access the full event experience.';
  return `${browseCopy} ${memberCopy}`;
}

export function buildWelcomeFeatureSteps(
  modules: readonly ResolvedEventModule[],
): WelcomeFeatureStep[] {
  const steps: WelcomeFeatureStep[] = [{
    kind: 'welcome',
    title: 'Welcome',
    description: 'Your companion for this event.',
  }];

  const agenda = enabledModules(modules, ['agenda']);
  if (agenda.length > 0) {
    steps.push({
      kind: 'agenda',
      title: `Personalized ${agenda[0].label}`,
      description: `Plan your event with ${agenda[0].label} and keep track of the sessions that matter to you.`,
    });
  }

  const ai = enabledModules(modules, ['ai_copilot']);
  if (ai.length > 0) {
    steps.push({
      kind: 'ai',
      title: ai[0].label,
      description: `Use ${ai[0].label} to explore the event and turn saved content into useful follow-ups.`,
    });
  }

  const library = enabledModules(modules, [
    'notes',
    'agenda',
    'papers',
    'speakers',
    'exhibitors',
  ]);
  if (library.length > 0) {
    steps.push({
      kind: 'library',
      title: 'Your Library',
      description: `Keep ${humanList(library.map((module) => module.label))} together in your personal library.`,
    });
  }

  const explore = enabledModules(modules, [
    'papers',
    'speakers',
    'exhibitors',
    'attendees',
  ]);
  if (explore.length > 0) {
    steps.push({
      kind: 'explore',
      title: 'Explore & Connect',
      description: `Discover ${humanList(explore.map((module) => module.label))} available at this event.`,
    });
  }

  return steps;
}
